import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Constants } from '../../../../../shared/interfaces/constants';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { CdkDropList } from '@angular/cdk/drag-drop';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable, Subject, debounceTime, distinctUntilChanged, startWith, take, takeUntil } from 'rxjs';
import { TagsDropAssignService } from '../../../../../shared/services/tags-drop-assign.service';
import { CommonModule } from '@angular/common';
import { MatSliderModule } from '@angular/material/slider';
import {
  ClientBusinessEntityControllerService,
	ManagementBusinessEntityControllerService,
	ManagementBusinessEntityResponse,
	ManagementMediumContractNoticesControllerService,
	ManagementMediumResponse,
} from 'api-contract-notices';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SharedService } from '../../../../../shared/services/shared.service';

@Component({
	selector: 'app-contract-notices-filters',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatCardModule,
		MatDividerModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		MatCheckboxModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatSelectModule,
		CdkDropList,
		MatAutocompleteModule,
		MatSliderModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './contract-notices-filters.component.html',
	styleUrl: './contract-notices-filters.component.scss',
})
export class ContractNoticesFiltersComponent implements OnInit, OnChanges, OnDestroy {
	private onDestroy: Subject<boolean> = new Subject();
	@Input() role!: 'admin' | 'client' | 'landing';
	@Input() dataAdministrationType!: 'CONTRACT_NOTICES' | 'CONTRACT_ANNOUNCEMENTS';
	@Input() resetFilters: any;
	@Input() apiLoading!: boolean;
	@Input() externalFilters: any;
	@Input() availableCountries!: any;
	// Autotag UI feature flag (backend `autotag.ui.enabled`) - gates the color/reviewed/risk-sort
	// filter controls so the filter bar looks exactly as today when it's false.
	@Input() autotagUiEnabled: boolean = false;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	// Frontend-only - matches Constants.TAG_SUGGESTION_REASON_CODES' colors 1-4, not the tag's own colorCode.
	autotagColors = [1, 2, 3, 4];

	instantSearch = false;

	filtersFormGroup: FormGroup = new FormGroup({});
	contractNoticesTypes = Constants.CONTRACT_NOTICE_TYPES;
	contractAnnouncementsTypes = Constants.CONTRACT_ANNOUNCEMENT_TYPES;
	countries = Constants.COUNTRIES.map(c => ({
		...c,
		availability: true,
	}));
	filteredContractors: ManagementBusinessEntityResponse[] | undefined;
	filteredContractAuhorities: ManagementBusinessEntityResponse[] | undefined;
	filteredMediums: ManagementMediumResponse[] | undefined;

	DAYS_RANGE = 0;
	DATE_RANGE_LIMITS = {
		dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
		dateTo: new Date(),
	};

	constructor(
		private fb: FormBuilder,
		public tagsDropAssign: TagsDropAssignService,
		public utils: UtilsService,
		private translate: TranslateService,
		private apiBusinessEntity: ManagementBusinessEntityControllerService,
    private apiClientBusinessEntity: ClientBusinessEntityControllerService,
		private apiMedium: ManagementMediumContractNoticesControllerService,
		private sharedService: SharedService
	) {}

	ngOnInit(): void {
		this.formInitializer();

		this.sharedService.requestActionInComponent.pipe(takeUntil(this.onDestroy)).subscribe({
			next: response => {
				if (response.componentName === 'ContractNoticesFiltersComponent') {
					switch (response.action) {
						case 'FILTER_TRIGGER': {
							const previousDataAdministrationType = JSON.parse(JSON.stringify(this.dataAdministrationType));
							this.dataAdministrationType = response.data.dataAdministrationType;
							if (!previousDataAdministrationType || previousDataAdministrationType !== this.dataAdministrationType) {
								this.filtersFormGroup.controls['types'].setValue(this.dataAdministrationType === 'CONTRACT_NOTICES' ? this.contractNoticesTypes : this.contractAnnouncementsTypes);
							}
							this.filterListItems();
							break;
						}
					}
				}
			},
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['availableCountries'] && changes['availableCountries'].currentValue) {
			// workaround: delay because value changes gives empty array
			setTimeout(() => {
				if (changes['availableCountries'].currentValue.length > 0) {
					this.countries = Constants.COUNTRIES.map(c => {
						const countryAvailability = changes['availableCountries'].currentValue.find((ac: any) => ac.countryCode === c.countryCode).availability;
						return {
							...c,
							availability: countryAvailability.find((c: string) => c.includes(this.dataAdministrationType)) ? true : false,
						};
					});
				}
				this.filtersFormGroup.controls['countries'].setValue(this.countries.filter(c => c.availability).map(c => c.countryCode));
				if (this.role === 'client') {
					this.filterListItems();
				}
			}, 100);
		}

		if (changes['resetFilters'] && !changes['resetFilters'].currentValue && !changes['resetFilters'].firstChange) {
			this.resetForm();
		}
		if (changes['role'] && changes['role'].currentValue === 'client') {
			this.DAYS_RANGE = 3;
			this.DATE_RANGE_LIMITS = {
				dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
				dateTo: new Date(),
			};
		}
		if (changes['externalFilters'] && changes['externalFilters'].currentValue) {
			if (
				Object.keys(changes['externalFilters'].currentValue).filter(key => changes['externalFilters'].currentValue[key] && changes['externalFilters'].currentValue[key].length)
					.length
			) {
				this.filtersFormGroup.patchValue(this.externalFilters);
				this.filterListItems();
			}
		}
		if (changes['dataAdministrationType'] && changes['dataAdministrationType'].currentValue) {
			this.filtersFormGroup.controls['types'].setValue(this.dataAdministrationType === 'CONTRACT_NOTICES' ? this.contractNoticesTypes : this.contractAnnouncementsTypes);

			if (this.role === 'client') {
				if (this.availableCountries && this.availableCountries.length > 0) {
					this.countries = Constants.COUNTRIES.map(c => {
						const countryAvailability = this.availableCountries.find((ac: any) => ac.countryCode === c.countryCode)?.availability || [];
						return {
							...c,
							availability: countryAvailability.find((availType: string) => availType.includes(this.dataAdministrationType)) ? true : false,
						};
					});
					this.filtersFormGroup.controls['countries'].setValue(this.countries.filter(c => c.availability).map(c => c.countryCode));
					this.filterListItems();
				}
			}
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next(true);
	}

	/** FORMS initializations, events, validation */
	private formInitializer() {
		this.filtersFormGroup = this.fb.group({
			types: [this.dataAdministrationType === 'CONTRACT_NOTICES' ? this.contractNoticesTypes : this.contractAnnouncementsTypes],
			contractAuthority: [],
			searchText: [],
			contractor: [],
			tagIds: [],
			createdAtFrom: [this.DATE_RANGE_LIMITS.dateFrom],
			createdAtTo: [this.DATE_RANGE_LIMITS.dateTo],
			deliveryDateFrom: [],
			deliveryDateTo: [],
			countries: [[]],
			estimatedPurchaseValueFrom: [0],
			estimatedPurchaseValueTo: [9],
			agreementValueFrom: [0],
			agreementValueTo: [9],
			searchAllCategories: [this.role === 'admin'],
			medium: [],

			flags: ['searchAllCategories'],
			tagsAll: [false],
			noTags: [false],
			onlyAdditional: [false],
			onlyFake: [false],

			// Autotag UI only (see autotagUiEnabled) - colors/reviewed go in the filter body,
			// sortBy is sent as a query param, not part of the body (see filterListItems()).
			colors: [[]],
			reviewed: [false],
			sortBy: ['createdAt'],
		});

		this.formControlWatchers();
	}

	private formControlWatchers() {
		if (this.filtersFormGroup.controls['contractAuthority']) {
			this.filtersFormGroup.controls['contractAuthority'].valueChanges
				.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					if (this.filtersFormGroup.controls['contractAuthority'].dirty) {
						this.searchBusinessEntities(searchTerm, 'contractAuthority');
					}
				});
		}

		if (this.filtersFormGroup.controls['contractor']) {
			this.filtersFormGroup.controls['contractor'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				if (this.filtersFormGroup.controls['contractor'].dirty) {
					this.searchBusinessEntities(searchTerm, 'contractor');
				}
			});
		}

		if (this.filtersFormGroup.controls['searchText']) {
			this.filtersFormGroup.controls['searchText'].valueChanges
				.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy))
				.subscribe(searchTerm => {
					if (this.filtersFormGroup.controls['searchText'].dirty && this.instantSearch) {
						this.filterListItemsDelayed();
					}
				});
		}

		if (this.filtersFormGroup.controls['medium']) {
			this.filtersFormGroup.controls['medium'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.searchMediums(searchTerm);
			});
		}
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.filtersFormGroup.reset();
		if (formDirective) {
			formDirective.resetForm();

			// For client role, restore original preselected tags instead of clearing them
			// For admin role, deselect all tags
			this.sharedService.requestActionInComponent.next({
				componentName: 'TagsComponent',
				action: this.role === 'client' ? 'RESELECT_ORIGINAL_TAGS' : 'DESELECT_TAGS',
			});
		}

		this.filtersFormGroup.controls['createdAtFrom'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
		this.filtersFormGroup.controls['createdAtTo'].setValue(this.DATE_RANGE_LIMITS.dateTo);
		this.filtersFormGroup.controls['tagsAll'].setValue(false);
		this.filtersFormGroup.controls['noTags'].setValue(false);
		this.filtersFormGroup.controls['onlyAdditional'].setValue(false);
		this.filtersFormGroup.controls['onlyFake'].setValue(false);
		this.filtersFormGroup.controls['flags'].setValue(['searchAllCategories']);
		this.filtersFormGroup.controls['estimatedPurchaseValueFrom'].setValue(0);
		this.filtersFormGroup.controls['estimatedPurchaseValueTo'].setValue(9);
		this.filtersFormGroup.controls['agreementValueFrom'].setValue(0);
		this.filtersFormGroup.controls['agreementValueTo'].setValue(9);
		this.filtersFormGroup.controls['searchAllCategories'].setValue(this.role === 'admin');
		this.filtersFormGroup.controls['types'].setValue(this.dataAdministrationType === 'CONTRACT_NOTICES' ? this.contractNoticesTypes : this.contractAnnouncementsTypes);
		this.filtersFormGroup.controls['countries'].setValue(this.countries.filter(c => c.availability).map(c => c.countryCode));
		this.filtersFormGroup.controls['colors'].setValue([]);
		this.filtersFormGroup.controls['reviewed'].setValue(false);
		this.filtersFormGroup.controls['sortBy'].setValue('createdAt');

		this.filterListItems();
	}
	/** (END)FORMS initializations, events, validation */

	/** delayed calling for filtering, because select field does not contain the checked value immediatelly */
	filterListItemsDelayed() {
		setTimeout(() => {
			this.filterListItems();
		}, 10);
	}

	selectTypeSubgroupItems(type: 'CONTRACT_NOTICE' | 'CONTRACT_ANNOUNCEMENT') {
		const currentSelection = this.filtersFormGroup.controls['types'].value;
		const currentSelFiltered: any = [];
		if (currentSelection && currentSelection.length) {
			let toggleVal = true;
			currentSelection.forEach((selectedItem: string) => {
				if (
					(type === 'CONTRACT_NOTICE' && this.contractNoticesTypes.indexOf(selectedItem) === -1) ||
					(type === 'CONTRACT_ANNOUNCEMENT' && this.contractAnnouncementsTypes.indexOf(selectedItem) === -1)
				) {
					currentSelFiltered.push(selectedItem);
				} else {
					toggleVal = false;
				}
			});

			if (toggleVal) {
				currentSelFiltered.push.apply(currentSelFiltered, type === 'CONTRACT_NOTICE' ? this.contractNoticesTypes : this.contractAnnouncementsTypes);
			}
			this.filtersFormGroup.controls['types'].setValue(currentSelFiltered);
		} else {
			this.filtersFormGroup.controls['types'].setValue(type === 'CONTRACT_NOTICE' ? this.contractNoticesTypes : this.contractAnnouncementsTypes);
		}

		if (this.instantSearch) {
			this.filterListItems();
		}
	}

	estimatedValueLabel(value: number, field?: string): string {
		const currencyMarks: Map<number, any> = new Map([
			[0, { label: '0', value: 0 }],
			[1, { label: '5.000', value: 5000 }],
			[2, { label: '10.000', value: 10000 }],
			[3, { label: '15.000', value: 15000 }],
			[4, { label: '20.000', value: 20000 }],
			[5, { label: '25.000', value: 25000 }],
			[6, { label: '50.000', value: 50000 }],
			[7, { label: '75.000', value: 75000 }],
			[8, { label: '100.000', value: 100000 }],
			[9, { label: '>100.000', value: 0 }],
		]);

		return currencyMarks.get(value)[field ? field : 'label'] || '';
	}

	/** Filter input business entity name */
  private getBusinessEntitySearchObservable(searchTerm: string): Observable<any> {
    switch (this.role) {
      case 'admin':
        return this.apiBusinessEntity.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
      case 'client':
        return this.apiClientBusinessEntity.getClientBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
      default:
        return this.apiBusinessEntity.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);

    }
  }

	private searchBusinessEntities(searchTerm: string, filter: 'contractAuthority' | 'contractor') {
		if (searchTerm && searchTerm.length >= 1) {
			this.getBusinessEntitySearchObservable(searchTerm)
				.pipe(take(1))
				.subscribe({
					next: response => {
						if (filter === 'contractAuthority') {
							this.filteredContractAuhorities = response.content;
						} else if (filter === 'contractor') {
							this.filteredContractors = response.content;
						}
						if (this.instantSearch) {
							this.filterListItems();
						}
					},
				});
		} else {
			if (this.instantSearch) {
				this.filterListItems();
			}
		}
	}
	displayFn(contractAuthority: ManagementBusinessEntityResponse): string {
		return contractAuthority && contractAuthority.id ? this.utils.getParameterByLanguageScript(contractAuthority, 'name', true) : '';
	}

	/** Filter input medium name */
	private searchMediums(searchTerm: string) {
		if (searchTerm && searchTerm.length >= 1) {
			this.apiMedium
				.searchMediums(searchTerm)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.filteredMediums = response;

						if (this.instantSearch) {
							this.filterListItems();
						}
					},
				});
		} else {
			if (this.instantSearch) {
				this.filterListItems();
			}
		}
	}
	displayMediumFn(medium: ManagementMediumResponse): string {
		return medium && medium.id ? this.utils.getParameterByLanguageScript(medium, 'name') : '';
	}

	filterListItems() {
		if (this.tagsDropAssign.manuallyselectedTags) {
			this.filtersFormGroup.controls['tagIds'].setValue(this.tagsDropAssign.manuallyselectedTags.map(tag => tag.id));
		}
		if (this.tagsDropAssign.manuallyselectedTags && this.role === 'client' && this.filtersFormGroup.controls['searchAllCategories'].value) {
			this.filtersFormGroup.controls['tagIds'].setValue([]);
		}

		const filterDto = JSON.parse(JSON.stringify(this.filtersFormGroup.value));
		filterDto.createdAtFrom = this.utils.setDateToDay(this.filtersFormGroup.controls['createdAtFrom'].value, 'START');
		filterDto.createdAtTo = this.utils.setDateToDay(this.filtersFormGroup.controls['createdAtTo'].value, 'END');
		filterDto.deliveryDateFrom = this.utils.setDateToDay(this.filtersFormGroup.controls['deliveryDateFrom'].value, 'START');
		filterDto.deliveryDateTo = this.utils.setDateToDay(this.filtersFormGroup.controls['deliveryDateTo'].value, 'END');
		filterDto.estimatedPurchaseValueTo = this.estimatedValueLabel(filterDto.estimatedPurchaseValueTo, 'value') || 0;
		filterDto.estimatedPurchaseValueFrom = this.estimatedValueLabel(filterDto.estimatedPurchaseValueFrom, 'value') || 0;
		filterDto.agreementValueFrom = this.estimatedValueLabel(filterDto.agreementValueFrom, 'value') || 0;
		filterDto.agreementValueTo = this.estimatedValueLabel(filterDto.agreementValueTo, 'value') || 0;

		filterDto.contractAuthorityId = filterDto.contractAuthority ? filterDto.contractAuthority.id : null;
		filterDto.contractorId = filterDto.contractor ? filterDto.contractor.id : null;
		filterDto.mediumId = filterDto.medium ? filterDto.medium.id : null;

		if (this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('withoutTag')) {
			filterDto.noTags = true;
		}

		if (this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('additionalMediums')) {
			filterDto.onlyAdditional = true;
		}

		if (this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('onlyFake')) {
			filterDto.onlyFake = true;
		} else {
			filterDto.onlyFake = false;
		}

		if (this.role === 'admin') {
			filterDto.autoTaggedAi = this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('autoTaggedAi') ? true : false;
			filterDto.autoTaggedSingleCriteria = this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('autoTaggedSingleCriteria') ? true : false;
			filterDto.autoTaggedMultipleCriteria = this.filtersFormGroup.controls['flags'].value && this.filtersFormGroup.controls['flags'].value.includes('autoTaggedMultipleCriteria') ? true : false;
		} else {
			filterDto.autoTaggedAi = false;
			filterDto.autoTaggedSingleCriteria = false;
			filterDto.autoTaggedMultipleCriteria = false;
		}

		// Autotag UI only. `sortBy` is a query param on the filter endpoint, not a body field, so
		// it's pulled out of filterDto and sent alongside it instead. `colors`/`reviewed` do stay
		// in the body. When the flag is off these controls are hidden and keep their defaults
		// ([]/false), so this is a no-op for everyone else.
		const sortBy: 'createdAt' | 'risk' = this.autotagUiEnabled ? filterDto.sortBy || 'createdAt' : 'createdAt';
		delete filterDto.sortBy;
		if (!this.autotagUiEnabled) {
			delete filterDto.colors;
			delete filterDto.reviewed;
		} else if (!filterDto.reviewed) {
			// "Непрегледани" unchecked = no filter on reviewed status at all, not "reviewed=false".
			delete filterDto.reviewed;
		} else {
			filterDto.reviewed = false;
		}

		this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0, sortBy });
	}
}
